import { useQuery } from "@tanstack/react-query";

import { staffQueryKeys } from "./query-keys";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useTimezoneConverter } from "../use-timezone-converter";
import { type UseStaffAvailabilityProps } from "./types";
import { useCallback } from "react";

interface StaffAvailability {
  startDateTime: string;
  endDateTime: string;
}

export const useStaffAvailability = (centerId: string, props: UseStaffAvailabilityProps) => {
  const { getToken } = useAccessToken();
  const { convertToDate } = useTimezoneConverter(centerId);
  const { appointmentTypeId, startDatetime, endDatetime } = props;

  return useQuery({
    queryKey: staffQueryKeys.availability(centerId, props),
    queryFn: async () => getStaffAvailability(await getToken(), centerId, props),
    enabled: !!(centerId && appointmentTypeId && startDatetime && endDatetime),
    select: useCallback(
      (data: StaffAvailability[]) =>
        data
          .map(dateRange => ({
            startDateTime: convertToDate(dateRange.startDateTime),
            endDateTime: convertToDate(dateRange.endDateTime)
          }))
          .filter(({ startDateTime }) => startDateTime && startDateTime > new Date()) as Array<{
          startDateTime: Date;
          endDateTime: Date;
        }>,
      [convertToDate]
    )
  });
};

const getStaffAvailability = (accessToken: string, centerId: string, queryParams: UseStaffAvailabilityProps) => {
  //TODO: URLSearchParams is expecting a string but can handle an array of strings
  //@ts-ignore
  const searchParams = new URLSearchParams(queryParams);
  return callLcosService<StaffAvailability[]>(
    accessToken,
    `/api/availability/${centerId}?${searchParams}`,
    "GET"
  );
};
